import { useRef, useEffect, useState, useCallback } from "react";
import FlowNode from "./FlowNode";
import {
  musicSystem,
  getNodeFromPosition,
  getTransitionsFromNode,
  getTransition,
} from "../databases/adaptiveMusicConfig";

// Define the node layout and connections (visual representation)
const NODE_CONFIG = {
  approach: { col: 0, row: 0, label: "Approach", shape: "circle" },
  branch: {
    col: 1,
    row: 0,
    label: "Branch",
    sublabel: "Decision",
    shape: "diamond",
  },
  exploration: {
    col: 2,
    row: -1,
    label: "Exploration",
    sublabel: "Low",
    shape: "circle",
  },
  tension: {
    col: 2,
    row: 1,
    label: "Tension",
    sublabel: "Rising",
    shape: "hexagon",
  },
  combat: {
    col: 3,
    row: 1,
    label: "Combat",
    sublabel: "High",
    shape: "hexagon",
  },
  resolution: {
    col: 4,
    row: 0,
    label: "Resolution",
    sublabel: "Victory",
    shape: "ring",
  },
  output: { col: 5, row: 0, label: "Output", sublabel: "Final", shape: "ring" },
};

// Define connections between nodes
const CONNECTIONS = [
  { from: "approach", to: "branch", type: "wait" },
  { from: "branch", to: "exploration", type: "wait" },
  { from: "branch", to: "tension", type: "immediate" },
  { from: "exploration", to: "resolution", type: "wait" },
  { from: "tension", to: "combat", type: "wait" },
  { from: "combat", to: "resolution", type: "wait" },
  { from: "resolution", to: "output", type: "wait" },
  { from: "resolution", to: "branch", type: "backward" }, // Loopback
];

// Grid settings
const COL_WIDTH = 100;
const ROW_HEIGHT = 60;
const GRID_PADDING = 60;
const LOOPBACK_ARC_PADDING = 80; // Extra space below nodes for loopback arc

const FlowVisualization = ({
  isLoaded,
  isPlaying,
  isTransitioning,
  pendingTransition,
  activeTracks,
  liveTimelinePos,
  transitionParam,
  onStart,
  onStop,
  onTransitionSelect,
}) => {
  // Get current node based on timeline position
  const currentNode = isPlaying ? getNodeFromPosition(liveTimelinePos) : null;
  // Get available transitions from current node
  const availableTransitions = currentNode
    ? getTransitionsFromNode(currentNode)
    : [];
  const containerRef = useRef(null);
  const nodeRefs = useRef({});
  const [nodePositions, setNodePositions] = useState({});

  // Calculate node positions based on grid
  const calculatePositions = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const positions = {};

    // Find grid bounds
    const cols = Object.values(NODE_CONFIG).map((n) => n.col);
    const rows = Object.values(NODE_CONFIG).map((n) => n.row);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);

    const gridWidth = (maxCol - minCol) * COL_WIDTH;
    const gridHeight = (maxRow - minRow) * ROW_HEIGHT;

    // Center the grid in the container
    const offsetX = (containerRect.width - gridWidth) / 2 - minCol * COL_WIDTH;
    const offsetY =
      (containerRect.height - gridHeight) / 2 - minRow * ROW_HEIGHT;

    Object.entries(NODE_CONFIG).forEach(([nodeId, config]) => {
      positions[nodeId] = {
        x: offsetX + config.col * COL_WIDTH,
        y: offsetY + config.row * ROW_HEIGHT,
      };
    });

    setNodePositions(positions);
  }, []);

  // Recalculate positions on mount and resize
  useEffect(() => {
    calculatePositions();
    window.addEventListener("resize", calculatePositions);
    return () => window.removeEventListener("resize", calculatePositions);
  }, [calculatePositions]);

  // Get node active states based on current timeline position
  const getNodeState = (nodeId) => {
    const isCurrentNode = currentNode === nodeId;

    // Special handling for approach (start) node
    if (nodeId === "approach") {
      return {
        isActive: isCurrentNode || (!isPlaying && isLoaded),
        intensity: isCurrentNode ? 3 : !isPlaying && isLoaded ? 1 : 0,
        sublabel: !isLoaded
          ? "Loading..."
          : isPlaying
            ? isCurrentNode
              ? "Playing"
              : ""
            : "Start",
      };
    }

    // Special handling for output (end) node
    if (nodeId === "output") {
      return {
        isActive: isCurrentNode,
        intensity: isCurrentNode ? 4 : 0,
        sublabel: isCurrentNode ? "Complete" : "Final",
      };
    }

    // Check if there's a pending transition targeting this node
    const hasIncomingPendingTransition =
      pendingTransition &&
      availableTransitions.some(
        (t) => t.to === nodeId && t.targetIndex === transitionParam,
      );

    return {
      isActive: isCurrentNode || hasIncomingPendingTransition,
      intensity: isCurrentNode ? 3 : hasIncomingPendingTransition ? 2 : 0,
    };
  };

  // Get connection active state and style
  const getConnectionState = (from, to, type) => {
    // Get the transition config for this connection
    const transition = getTransition(from, to);

    // Is this connection from the current node?
    const isFromCurrentNode = currentNode === from;

    // Is there a pending transition for this exact connection?
    const isPendingConnection =
      pendingTransition &&
      transition &&
      transition.targetIndex === transitionParam;

    // For backward (loopback) connections
    if (type === "backward") {
      const isActive = isPendingConnection;
      return {
        stroke: isActive ? "#9e6b4a" : "rgba(158,107,74,0.3)",
        dashArray: "4 4",
        active: isActive,
      };
    }

    // For connections from the current node (potential transitions)
    if (isFromCurrentNode && isPlaying) {
      if (isPendingConnection) {
        // This specific transition is armed
        return {
          stroke: "#b59e4a",
          dashArray: "8 4",
          active: true,
        };
      }
      // Available transition (can be selected)
      return {
        stroke: "rgba(74,158,181,0.6)",
        dashArray: "none",
        active: true,
      };
    }

    // Default inactive state
    return {
      stroke: "rgba(74,158,181,0.25)",
      dashArray: "none",
      active: false,
    };
  };

  // Check if we have valid positions
  const hasPositions = Object.keys(nodePositions).length > 0;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        position: "relative",
        minHeight: "280px",
        padding: "40px 20px",
      }}
    >
      {/* SVG Layer for Connections */}
      {hasPositions && (
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {CONNECTIONS.map(({ from, to, type }) => {
            const fromPos = nodePositions[from];
            const toPos = nodePositions[to];
            if (!fromPos || !toPos) return null;

            const { stroke, dashArray, active } = getConnectionState(
              from,
              to,
              type,
            );

            // Check connection type
            const isLoopback = toPos.x < fromPos.x; // Going backwards (right to left)
            const isDiagonal = fromPos.y !== toPos.y && !isLoopback;
            const curveAmount = COL_WIDTH;

            let pathD;
            if (isLoopback) {
              // Loopback: semi-circular arc below the node system
              // Find the lowest node position for arc placement
              const allYPositions = Object.values(nodePositions).map(
                (p) => p.y,
              );
              const lowestY = Math.max(...allYPositions);

              // Calculate arc parameters
              const midX = (fromPos.x + toPos.x) / 2;
              const horizontalDist = fromPos.x - toPos.x;
              const arcRadius = horizontalDist / 2;
              const arcBottomY = lowestY + LOOPBACK_ARC_PADDING;

              // Create semi-circular arc using SVG arc command
              // A rx ry rotation large-arc-flag sweep-flag x y
              // sweep-flag=1 for clockwise (going below in SVG coords)
              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${fromPos.x},${arcBottomY - arcRadius}
                                     A ${arcRadius} ${arcRadius} 0 0 1 ${toPos.x},${arcBottomY - arcRadius}
                                     L ${toPos.x},${toPos.y}`;
            } else if (isDiagonal) {
              // For diagonals: straight first, then curve at the end
              const curveStartX = toPos.x - curveAmount;

              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${curveStartX},${fromPos.y}
                                     C ${curveStartX + curveAmount * 0.5},${fromPos.y}
                                       ${toPos.x - curveAmount * 0.3},${toPos.y}
                                       ${toPos.x},${toPos.y}`;
            } else {
              // Straight horizontal line
              pathD = `M ${fromPos.x},${fromPos.y} L ${toPos.x},${toPos.y}`;
            }

            // Pulse color based on connection type
            const pulseColor = type === "backward" ? "#9e6b4a" : "#4a9eb5";

            return (
              <g key={`${from}-${to}`}>
                <path
                  d={pathD}
                  fill="none"
                  stroke={stroke}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={dashArray === "none" ? undefined : dashArray}
                />
                {/* Animated pulse along path */}
                {active && (
                  <circle r="3" fill={pulseColor}>
                    <animateMotion
                      dur={type === "backward" ? "2.5s" : "1.5s"}
                      repeatCount="indefinite"
                      path={pathD}
                    />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* Node Layer */}
      {hasPositions &&
        Object.entries(NODE_CONFIG).map(([nodeId, config]) => {
          const pos = nodePositions[nodeId];
          if (!pos) return null;

          const state = getNodeState(nodeId);

          // Click handlers for each node
          let onClick;
          if (nodeId === "approach") {
            // Start/stop button
            onClick = () => {
              isPlaying ? onStop() : isLoaded && onStart();
            };
          } else if (isPlaying && !isTransitioning) {
            // Check if there's a transition from current node to this node
            const transition = getTransition(currentNode, nodeId);
            if (transition && onTransitionSelect) {
              onClick = () => onTransitionSelect(transition.targetIndex);
            }
          }

          return (
            <div
              key={nodeId}
              ref={(el) => (nodeRefs.current[nodeId] = el)}
              style={{
                position: "absolute",
                left: pos.x,
                top: pos.y,
                transform: "translate(-50%, -50%)",
              }}
            >
              <FlowNode
                id={nodeId}
                label={config.label}
                sublabel={state.sublabel || config.sublabel}
                shape={config.shape}
                isActive={state.isActive}
                intensity={state.intensity}
                onClick={onClick}
              />
            </div>
          );
        })}

      {/* Status Row */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: "10px",
            color: pendingTransition ? "#4a9eb5" : "#6a7080",
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            margin: 0,
          }}
        >
          {!isLoaded
            ? "Loading..."
            : isTransitioning
              ? "Transitioning..."
              : pendingTransition
                ? `Transition Armed - ${currentNode}`
                : isPlaying
                  ? `${currentNode} - ${liveTimelinePos.toFixed(1)}s`
                  : "Click Approach to start"}
        </p>
        {/* Show available transitions */}
        {isPlaying && !isTransitioning && availableTransitions.length > 0 && (
          <p
            style={{
              fontSize: "9px",
              color: "#4a5060",
              letterSpacing: "0.1em",
              margin: "4px 0 0 0",
            }}
          >
            Transitions: {availableTransitions.map((t) => t.to).join(", ")}
          </p>
        )}
      </div>
    </div>
  );
};

export default FlowVisualization;
